import { callAPI } from './api.service';
import { encryptToken } from '../helper/helper';
import { TokenCookieManager } from '../utils/tokenCookies';
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { setRedirectURL } from '../store/userSlice';
import { autoFetchUserDetails } from '../store/thunks/user.thunk';
import { HTTPStatusEnum } from '../interfaces/log.interface';
import { Toast } from '../components/Common/Toast';

export interface CleverAuthResponse {
  status: number;
  token: string;
  user: any;
  isNewUser: boolean;
  message: string;
}

export interface CleverAuthUrlResponse {
  status: number;
  authUrl: string;
}

export enum CleverAuthType {
  TEACHER_SIGN_UP = 'teacher-sign-up',
  TEACHER_LOGIN = 'teacher-login',
  STUDENT_LOGIN = 'student-login',
}

export class CleverService {
  /**
   * Generate Clever OAuth authorization URL
   */
  static async generateAuthUrl(type: CleverAuthType, isPopup: boolean = false): Promise<string> {
    try {
      const params = new URLSearchParams();
      params.append('type', type);
      if (isPopup) {
        params.append('popup', 'true');
      }
      const apiURL = 'api/auth/clever/url';

      const response = await callAPI(`${apiURL}?${params.toString()}`);

      if (response?.status === 200 && response?.authUrl) {
        return response.authUrl;
      }

      throw new Error('Failed to generate authorization URL');
    } catch (error: any) {
      console.error('Error generating Clever auth URL:', error);
      throw new Error('Failed to generate authorization URL');
    }
  }

  /**
   * Initiate Clever OAuth flow
   */
  static async initiateAuth(type: CleverAuthType): Promise<void> {
    try {
      const authUrl = await this.generateAuthUrl(type);

      // Redirect to Clever OAuth page
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Error initiating Clever auth:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback with authorization code
   */
  static async handleCallback(code: string, state?: string): Promise<CleverAuthResponse> {
    try {
      const apiURL = 'api/auth/clever/callback';

      const response = await callAPI(apiURL, {
        code,
        state,
      });

      if (response?.status === HTTPStatusEnum.OK) {
        return response;
      } else {
        Toast(response?.error || 'Authentication failed', true);
      }

      throw new Error(response?.error || 'Authentication failed');
    } catch (error: any) {
      console.error('Error handling Clever callback:', error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for token only (for popup authentication)
   */
  static async exchangeCode(code: string, state?: string): Promise<any> {
    try {
      const apiURL = 'api/auth/clever/exchange';

      const response = await callAPI(
        apiURL,
        {
          code,
          state,
        },
        'POST',
      );

      if (response?.status === HTTPStatusEnum.OK) {
        return response;
      } else {
        throw new Error(response?.error || 'Token exchange failed');
      }
    } catch (error: any) {
      console.error('Error exchanging code for token:', error);
      throw error;
    }
  }

  /**
   * Direct Clever signin with authorization code
   */
  static async signIn(code: string): Promise<CleverAuthResponse> {
    try {
      const response = await callAPI('api/auth/clever/signin', { code });

      if (response?.status === 200) {
        return response;
      }

      throw new Error(response?.error || 'Signin failed');
    } catch (error: any) {
      console.error('Error in Clever signin:', error);
      throw error;
    }
  }

  /**
   * Link existing account to Clever
   */
  static async linkAccount(email: string, code: string): Promise<any> {
    try {
      const response = await callAPI('api/auth/clever/link', {
        email,
        code,
      });

      if (response?.status === 200) {
        return response;
      }

      throw new Error(response?.error || 'Failed to link account');
    } catch (error: any) {
      console.error('Error linking account to Clever:', error);
      throw error;
    }
  }

  /**
   * Unlink Clever account
   */
  static async unlinkAccount(email: string): Promise<any> {
    try {
      const response = await callAPI(
        'api/auth/clever/unlink',
        {
          email,
        },
        'DELETE',
      );

      if (response?.status === 200) {
        return response;
      }

      throw new Error(response?.error || 'Failed to unlink account');
    } catch (error: any) {
      console.error('Error unlinking Clever account:', error);
      throw error;
    }
  }

  /**
   * Log out from Clever
   */
  static async logoutFromCleverSync(): Promise<boolean> {
    try {
      const response = await callAPI('api/auth/clever/logout', null, 'POST');

      if (response?.status === HTTPStatusEnum.OK) {
        this.clearCleverData();
        return true;
      }

      throw new Error(response?.error || 'Failed to log out from Clever');
    } catch (error: any) {
      console.error('Error refreshing Clever token:', error);
      throw error;
    }
  }

  /**
   * Complete Clever authentication flow
   */
  static async completeAuth(authResponse: CleverAuthResponse, dispatch: any): Promise<void> {
    try {
      console.log('Starting Firebase authentication with custom token');

      const userCredential: any = await signInWithCustomToken(auth, authResponse.token);
      // Encrypt the token
      const encryptedToken = encryptToken(userCredential?.user?.accessToken, process.env.REACT_APP_TOKEN_ENCRYPT_KEY!);

      // Store the token in cookies
      TokenCookieManager.setToken(encryptedToken);
      dispatch(autoFetchUserDetails());
      setTimeout(() => dispatch(setRedirectURL('/')), 250);
      return;
    } catch (error: any) {
      console.error('Error completing Clever authentication:', error);

      // Clean up any partial state
      TokenCookieManager.removeToken();

      throw new Error(`Firebase authentication failed: ${error.message}`);
    }
  }

  /**
   * Check if user is authenticated via Clever
   */
  static isCleverUser(): boolean {
    const userData = localStorage.getItem('clever_user_data');
    return !!userData;
  }

  /**
   * Get Clever user data
   */
  static getCleverUserData(): any {
    const userData = localStorage.getItem('clever_user_data');
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Clear Clever authentication data
   */
  static clearCleverData(): void {
    localStorage.removeItem('clever_user_data');
    localStorage.removeItem('clever_oauth_state');
  }
}

export const cleverService = new CleverService();
